import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REORDER_ERROR_MESSAGES, saveReorder } from '../../services/matchingApi.js';
import { readScoreOverride, withScoreOverride } from './scoreOverride';

// 最後に動かしてから (前の保存の応答を受け取ったのが後なら、そこから) 保存を送るまで待つ時間。
// 続けて動かす間は送らず、手を止めた後に 1 回だけ送る。前の応答から数えることで、DB の秒 (created_at) が
// 前の保存と必ず違う値になり、サーバの実体の時計に頼らない (#2462 comment 6632731228・6633455510)
export const REORDER_SAVE_DELAY_MS = 1200;

// 並べ替えの横の文 (SCR-M-001 §4.3。BTN-M-004 があった場所。#2462 の DESIGN の案 comment 6632755182 の 1.)
export const REORDER_STATUS_MESSAGES = {
    saving: '保存中...', // #1
    justSaved:
        '並び順を保存し、マッチ度を上から高い順に付け直しました。'
        + 'AI の値と異なる数字には「手入力」と表示されます。', // #3
    idle: '並べ替えると自動で保存し、マッチ度も上から高い順に付け直します。', // #4
};

/**
 * 並べ替えの横の文 (role="status" で出すもの) を決める。当てはまるもののうち番号の小さいもの (強いもの)
 * を返す。保存が失敗した後 (#2) は、失敗の文言を role="alert" で出すため null を返す。
 *
 * @param {{saving: boolean, failed: boolean, justSaved: boolean}} state saving は待っている間を含む
 * @returns {string|null}
 */
export function reorderStatusText({ saving, failed, justSaved }) {
    if (saving) return REORDER_STATUS_MESSAGES.saving;
    if (failed) return null;
    return justSaved ? REORDER_STATUS_MESSAGES.justSaved : REORDER_STATUS_MESSAGES.idle;
}

/**
 * 応答の推薦結果の要素で、推薦の要素 1 件のマッチ度 (AI の値と、手で直した値の有無) を入れ替えた
 * 新しい要素を返す。応答に同じ求人の要素が無ければ元の要素のまま。マッチ度以外の値 (並び・IN-M-011・
 * おすすめポイントの文) は入れ替えない (SCR-M-001 §6.2「並べ替えの保存でマッチ度を付け直す」手順 4)。
 *
 * @param {object} rec 推薦の要素 (推薦結果の recommendations、または画面の並びの要素)
 * @param {Map<string, object>} savedById 応答の要素を求人 ID で引く表
 * @returns {object}
 */
export function withRenumberedScore(rec, savedById) {
    const saved = savedById.get(rec.job_pool_item_id);
    if (!saved) return rec;
    const replaced = withScoreOverride(rec, readScoreOverride(saved));
    return typeof saved.score === 'number' ? { ...replaced, score: saved.score } : replaced;
}

const idsOf = (items) => items.map((item) => item.job_pool_item_id);
const sameOrder = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * 並べ替えの自動の保存。並びを動かしたら待ってから保存し、保存できたら応答の推薦結果でマッチ度を入れ替える
 * (画面レイアウト_SCR-M-001.md §6.2「並べ替えの保存でマッチ度を付け直す」、Issue #443・#2173・#2462)。
 *
 * - 動かしたら (notifyMoved)、その時点から「保存中」とする (待っている間を含む)
 * - 送るのは、最後に動かしたときと前の保存の応答を受け取ったときの遅い方から REORDER_SAVE_DELAY_MS 後。
 *   続けて動かすと待ち直す。前の保存の応答を受け取るまで次を送らない (送った順と保存された順を入れ替えない)
 * - ほかの保存の保存中・マッチ度の数字の編集中は送らず、それが終わってから待ち始める
 * - 並びが保存済みの並びと同じに戻ったら送らない
 * - 失敗したら止め、次に動かすか「保存し直す」(retry) で送り直す
 *
 * useAiMatching から呼ぶ (useAiMatching.js を大きくしないために分けた)。
 *
 * 保存中・待っている間・失敗の文言・保存できた直後は、どの推薦結果に対するものかを一緒に持つ。保存の間に
 * 表示する結果が替わったとき、前の結果の状態と応答の値を新しい結果に入れないため (§6.2 手順 6)。
 *
 * @param {object|null} matchingResult 表示中の推薦結果
 * @param {Function} setMatchingResult
 * @param {object[]} orderedItems 画面の並び
 * @param {Function} setOrderedItems
 * @param {string[]|null} savedOrderIds 保存済みの並び (表示したとき、または前回保存したときの並び)
 * @param {Function} setSavedOrderIds
 * @param {{current: boolean}|null} otherSavingRef マッチ度・求職者に提案するか・おすすめポイントの文の
 *   どれかの保存中か (送る時点の値で判定する。§6.2「同時に保存するもの」)
 * @param {{current: boolean}} savingRef 本フックの保存中 (応答を待っている間) を書き込む ref。ほかの保存のフックが
 *   押した時点で読む
 * @param {{current: boolean}} waitingRef 本フックの「待っている間」(動かしてから送るまで) を書き込む ref。
 *   ほかの保存のフックが押した時点で読む (待っている間も、ほかの保存・編集を始めない。#2462 の DESIGN の案)
 * @param {boolean} scoreEditing マッチ度の数字の編集中か。その間は送らない
 * @param {boolean} otherSaving otherSavingRef と同じことを state で持つもの。終わったときに待ち始めるために使う
 */
function useReorderSave(
    matchingResult,
    setMatchingResult,
    orderedItems,
    setOrderedItems,
    savedOrderIds,
    setSavedOrderIds,
    otherSavingRef,
    savingRef,
    waitingRef,
    scoreEditing,
    otherSaving = false
) {
    const shownResultId = matchingResult?.id ?? null;

    // 時間をおいて読む値 (タイマー・応答の後で、最新の値を読むため)
    const latestRef = useRef({});
    latestRef.current = { matchingResult, orderedItems, savedOrderIds, scoreEditing };

    // 待っている結果 ({ resultId })・保存中の結果 ({ resultId })・失敗 ({ resultId, message, retryable })・
    // 保存できた直後 ({ resultId })
    const [waiting, setWaitingState] = useState(null);
    // state と同時に ref にも書く (ほかの保存のフックは、描画を待たずに押した時点の値で判定するため)
    const setWaiting = useCallback((value) => {
        waitingRef.current = value !== null;
        setWaitingState(value);
    }, [waitingRef]);
    const [saving, setSaving] = useState(null);
    const [failure, setFailure] = useState(null);
    const [justSaved, setJustSaved] = useState(null);

    const timerRef = useRef(null);
    const lastMoveAtRef = useRef(0);
    const lastResponseAtRef = useRef(0);
    // 動かしたときに表示していた結果。送る時点で表示中の結果と違えば送らない
    const targetIdRef = useRef(null);
    // 「戻る」で、応答を待っている間に動かした分を、応答を受け取ったらすぐ送る
    const flushRequestedRef = useRef(false);
    // 送る処理 (下で定義。schedule から呼ぶため ref で持つ)
    const sendRef = useRef(() => {});

    const forShown = (state) => (state && state.resultId === shownResultId ? state : null);

    const isReorderDirty = useMemo(() => {
        if (!savedOrderIds) return false;
        return !sameOrder(idsOf(orderedItems), savedOrderIds);
    }, [orderedItems, savedOrderIds]);

    const clearTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    // 最後に動かしたときと前の応答を受け取ったときの遅い方から、待つ時間がたったら送る
    const schedule = useCallback(() => {
        clearTimer();
        const due = Math.max(lastMoveAtRef.current, lastResponseAtRef.current) + REORDER_SAVE_DELAY_MS;
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            sendRef.current();
        }, Math.max(0, due - Date.now()));
    }, [clearTimer]);

    // 今の並びを送る。force はほかの保存中でも送る (「戻る」で画面を離れるとき。サーバは同じ推薦結果の行を
    // ロックして書くため、重なっても順に処理される)
    const send = useCallback(async ({ force = false } = {}) => {
        clearTimer();
        const { matchingResult: result, orderedItems: items, savedOrderIds: savedIds, scoreEditing: editing } =
            latestRef.current;
        const targetId = targetIdRef.current;
        if (!result || result.id !== targetId) {
            // 表示する結果が替わった。前の結果の並びは送らない
            setWaiting(null);
            return;
        }
        // 前の保存の応答を待っている間は送らない (応答を受け取ったら送り直す)
        if (savingRef.current) return;
        // ほかの保存中・数字の編集中は送らない (それが終わったら待ち始める。下の effect)
        if (!force && (otherSavingRef?.current || editing)) return;
        const orderedIds = idsOf(items);
        if (sameOrder(orderedIds, savedIds)) {
            setWaiting(null);
            return;
        }

        savingRef.current = true;
        flushRequestedRef.current = false;
        setSaving({ resultId: targetId });
        setFailure(null);
        setJustSaved(null);

        let response;
        try {
            response = await saveReorder(targetId, orderedIds);
        } catch (err) {
            // 失敗は saveReorder が結果で返す。ここに来るのは想定外の例外だけ
            console.error('useReorderSave: 並べ替えの保存で例外が発生しました', err);
            response = { ok: false, errorMessage: REORDER_ERROR_MESSAGES.network, retryable: true };
        } finally {
            // 保存中の印は必ず戻す (戻らないと、以後すべての保存が送られず、エラーとしても見えない)
            savingRef.current = false;
            lastResponseAtRef.current = Date.now();
            setSaving(null);
        }

        const shownNow = latestRef.current.matchingResult?.id ?? null;
        if (!response.ok) {
            flushRequestedRef.current = false;
            clearTimer();
            setWaiting(null);
            if (shownNow === targetId) {
                setFailure({ resultId: targetId, message: response.errorMessage, retryable: Boolean(response.retryable) });
            }
            return;
        }

        const savedById = new Map(response.recommendations.map((rec) => [rec.job_pool_item_id, rec]));
        const applySaved = (rec) => withRenumberedScore(rec, savedById);
        // 保存の間に別の結果へ替わっていたら入れない。並びは応答で置き換えない (手順 4)
        setMatchingResult((prev) =>
            prev && prev.id === targetId && Array.isArray(prev.recommendations)
                ? { ...prev, recommendations: prev.recommendations.map(applySaved) }
                : prev
        );
        if (shownNow !== targetId) {
            setWaiting(null);
            return;
        }
        setOrderedItems((prev) => prev.map(applySaved));
        setSavedOrderIds(orderedIds);
        // 次の判定で使うため、描画を待たずに保存済みの並びを入れておく
        latestRef.current = { ...latestRef.current, savedOrderIds: orderedIds };

        if (!sameOrder(idsOf(latestRef.current.orderedItems), orderedIds)) {
            // 応答を待っている間に動かした。その時点の並びを送る (間の並びは送らない)
            if (flushRequestedRef.current) {
                sendRef.current({ force: true });
            } else {
                schedule();
            }
            return;
        }
        setWaiting(null);
        setJustSaved({ resultId: targetId });
    }, [
        clearTimer, otherSavingRef, savingRef, schedule, setWaiting, setMatchingResult, setOrderedItems, setSavedOrderIds,
    ]);
    sendRef.current = send;

    // 並びを動かした (↑↓・ドラッグを離した)。「保存できた直後」の文と失敗の文言を消し、待ち直す
    const notifyMoved = useCallback(() => {
        setJustSaved(null);
        setFailure(null);
        if (!matchingResult) return;
        lastMoveAtRef.current = Date.now();
        targetIdRef.current = matchingResult.id;
        setWaiting({ resultId: matchingResult.id });
        // 応答を待っている間は、応答を受け取ってから待ち始める
        if (!savingRef.current) schedule();
    }, [matchingResult, savingRef, schedule, setWaiting]);

    // 並びが保存済みの並びと同じに戻ったら送らない (待っている間だけ。保存中は応答の後で判定する)
    useEffect(() => {
        if (waiting && !isReorderDirty && !savingRef.current) {
            clearTimer();
            setWaiting(null);
        }
    }, [waiting, isReorderDirty, savingRef, clearTimer, setWaiting]);

    // ほかの保存・数字の編集が終わったら、そこから待ち始める (待っている並びを黙って送らずに終わらせない)
    const busy = otherSaving || scoreEditing;
    const prevBusyRef = useRef(busy);
    useEffect(() => {
        const wasBusy = prevBusyRef.current;
        prevBusyRef.current = busy;
        if (wasBusy && !busy && waiting && !savingRef.current) {
            lastResponseAtRef.current = Date.now();
            schedule();
        }
    }, [busy, waiting, savingRef, schedule]);

    // 画面を閉じたら待つのをやめる (「戻る」は先に flush を呼ぶ)
    useEffect(() => clearTimer, [clearTimer]);

    // 「保存し直す」(BTN-M-019)。待たずに、その時点の画面の並びを送る
    const retry = useCallback(() => {
        if (!matchingResult || savingRef.current) return;
        setFailure(null);
        targetIdRef.current = matchingResult.id;
        setWaiting({ resultId: matchingResult.id });
        send();
    }, [matchingResult, savingRef, send, setWaiting]);

    // 「戻る」(BTN-M-001)。待っている保存を待たずに送る (応答は待たない。画面を移った後も要求は続く)。
    // 応答を待っている間に動かした分は、応答を受け取ったらすぐ送る
    const flush = useCallback(() => {
        if (!waiting) return;
        if (savingRef.current) {
            flushRequestedRef.current = true;
            return;
        }
        send({ force: true });
    }, [waiting, savingRef, send]);

    // AI マッチングを実行したときに、失敗の文言と「保存できた直後」の文を消す (useAiMatching の実行が呼ぶ)
    const clearMessages = useCallback(() => {
        setFailure(null);
        setJustSaved(null);
    }, []);

    const failureForShown = forShown(failure);

    return {
        isReorderDirty,
        // 待っている間を含む。どの結果の保存でも真 (保存を重ねて送らない。ほかの保存のフックと同じ)
        reorderSaving: saving !== null || waiting !== null,
        reorderError: failureForShown ? failureForShown.message : null,
        reorderRetryable: failureForShown ? failureForShown.retryable : false,
        reorderJustSaved: forShown(justSaved) !== null,
        notifyReorderMoved: notifyMoved,
        clearReorderMessages: clearMessages,
        retryReorderSave: retry,
        flushReorderSave: flush,
    };
}

export default useReorderSave;
